import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { CareConciergeFormData } from './types';
import { formatUSPhone } from '@/shared/utils/formatters';

/**
 * Feature 104: client contact details on the FA workflow — the team's only
 * path to the care seeker when the advisor answers "No" to staying looped in,
 * hence required exactly then. Each field carries a "preferred contact
 * method" radio (one shared selection).
 */
type ClientContactFieldsProps = {
  register: UseFormRegister<CareConciergeFormData>;
  errors: FieldErrors<CareConciergeFormData>;
  /** Raw watched value — the stay-looped radios submit 'true'/'false' strings. */
  advisorStayLooped?: boolean | string;
};

const inputClasses =
  'w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm';

const ClientContactFields: React.FC<ClientContactFieldsProps> = ({
  register,
  errors,
  advisorStayLooped,
}) => {
  const required = advisorStayLooped === false || advisorStayLooped === 'false';

  // Compose the format-on-type handler with react-hook-form's own onChange —
  // overriding it outright would keep RHF from ever seeing the typed value.
  const phoneRegistration = register('clientPhone', {
    required: required
      ? 'Client phone is required when you opt out of staying looped in'
      : false,
    pattern: {
      value: /^\(\d{3}\) \d{3}-\d{4}$/,
      message: 'Enter a valid US phone number',
    },
  });

  return (
    <>
      <div>
        <label
          htmlFor="fa-client-phone"
          className="block text-sm font-termina font-medium text-[#111827] mb-1"
        >
          Client Phone Number {required && <span className="text-red-500">*</span>}
        </label>
        <input
          id="fa-client-phone"
          type="tel"
          {...phoneRegistration}
          onChange={(e) => {
            e.target.value = formatUSPhone(e.target.value);
            void phoneRegistration.onChange(e);
          }}
          className={inputClasses}
        />
        <label className="mt-2 flex items-center gap-2">
          <input
            type="radio"
            value="PHONE"
            {...register('clientPreferredContactMethod', {
              required: required
                ? "Please select the client's preferred contact method"
                : false,
            })}
            className="w-4 h-4 text-[#026857] border-[#E5E7EB] focus:ring-[#026857]"
          />
          <span className="font-termina text-xs text-[#4B5563]">Preferred contact method</span>
        </label>
        {errors.clientPhone && (
          <p className="mt-1 text-sm text-red-600 font-termina">{errors.clientPhone.message}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="fa-client-email"
          className="block text-sm font-termina font-medium text-[#111827] mb-1"
        >
          Client Email Address {required && <span className="text-red-500">*</span>}
        </label>
        <input
          id="fa-client-email"
          type="email"
          {...register('clientEmail', {
            required: required
              ? 'Client email is required when you opt out of staying looped in'
              : false,
          })}
          className={inputClasses}
        />
        <label className="mt-2 flex items-center gap-2">
          <input
            type="radio"
            value="EMAIL"
            {...register('clientPreferredContactMethod', {
              required: required
                ? "Please select the client's preferred contact method"
                : false,
            })}
            className="w-4 h-4 text-[#026857] border-[#E5E7EB] focus:ring-[#026857]"
          />
          <span className="font-termina text-xs text-[#4B5563]">Preferred contact method</span>
        </label>
        {errors.clientEmail && (
          <p className="mt-1 text-sm text-red-600 font-termina">{errors.clientEmail.message}</p>
        )}
        {errors.clientPreferredContactMethod && (
          <p className="mt-1 text-sm text-red-600 font-termina">
            {errors.clientPreferredContactMethod.message}
          </p>
        )}
      </div>
    </>
  );
};

export default ClientContactFields;
